import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Skeleton,
  Box,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import axios from 'axios';

const ShowTrackListHeader = ({ id, episodeCount, onPlayFirst, isPlayingFirst, loadingFirst }) => {
  const [showDetails, setShowDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [scale, setScale] = useState(1);
  const descriptionRef = useRef(null);

  useEffect(() => {
    const fetchShowDetails = async () => {
      try {
        const response = await axios.get(
          `https://broadcast.wssr.stream/api/station/1/public/podcast/${id}`
        );
        setShowDetails(response.data);
      } catch (error) {
        console.error('Error fetching show details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchShowDetails();
  }, [id]);

  useEffect(() => {
    if (!loading && descriptionRef.current) {
      const { scrollHeight, clientHeight } = descriptionRef.current;
      setIsTruncated(scrollHeight > clientHeight);
    }
  }, [loading, showDetails?.description]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      if (scrollTop > 100) {
        const newScale = Math.max(0.9, 1 - (scrollTop - 100) / 500);
        setScale(newScale);
      } else {
        setScale(1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  if (loading) {
    return (
      <Box
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
          marginBottom: '1.5rem',
        }}
      >
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '20px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
        marginBottom: '1.5rem',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}
      >
        <img
          src={showDetails?.art}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(30px) brightness(0.5)',
            transform: 'scale(1.15)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.8))',
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: '1200px',
          margin: '0 auto',
          padding: { xs: '2rem 1.5rem', md: '3rem 2rem' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'flex-start' },
          gap: { xs: 2, md: 3 },
          transform: `scale(${scale})`,
          transformOrigin: 'top',
          transition: 'transform 0.6s ease, margin 0.6s ease',
        }}
      >
        <Box
          sx={{
            flex: '0 0 auto',
            width: { xs: '160px', md: '220px' },
          }}
        >
          <Box sx={{ width: '100%', paddingTop: '100%', position: 'relative' }}>
            <img
              src={showDetails?.art}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '4px',
                boxShadow: '5px 5px 0px 0px rgba(255,102,0,1), 10px 10px 0px 0px rgba(25,35,48,1)',
              }}
              alt={showDetails?.title || 'Show Art'}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            textAlign: { xs: 'center', md: 'left' },
            width: '100%',
            color: 'white',
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#fff' }}>
            {showDetails?.title || 'Unknown Title'}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.7)', marginTop: '0.5rem' }}>
            {episodeCount} {episodeCount === 1 ? 'Episode' : 'Episodes'}
          </Typography>

          <Typography
            ref={descriptionRef}
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              marginTop: '1rem',
              display: expanded ? 'block' : '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: expanded ? 'none' : 3,
              overflow: 'hidden',
            }}
          >
            {showDetails?.description || 'No description available.'}
          </Typography>

          {isTruncated && (
            <IconButton
              onClick={handleExpandClick}
              size="small"
              sx={{
                color: 'white',
                alignSelf: { xs: 'center', md: 'flex-start' },
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          )}

          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: '999px', marginTop: '1rem', paddingX: '1.5rem' }}
            startIcon={
              loadingFirst ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : isPlayingFirst ? (
                <PauseCircleIcon />
              ) : (
                <PlayCircleIcon />
              )
            }
            onClick={onPlayFirst}
            disabled={!episodeCount}
          >
            {isPlayingFirst ? 'Pause' : 'Play'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ShowTrackListHeader;
